import React, { useContext, useMemo, useState, useEffect } from 'react'
import _ from 'lodash'
import { CacheChecksumContext, createPersistedState } from '../../cache'
import { cancelRequest, getAllDataInParallel } from '../../helpers'
import BrandsContext from './brands-context'
import { fetchBrands } from './brands-api'
import { useSystemSettings } from '../../system-settings'

// create use state hook with persisted storage
// TODO: support using different storage provider
const useBrandsState = createPersistedState('brands')
const useBrandsChecksumState = createPersistedState('brandsChecksum')

export default function BrandsProvider({ children, config, currentLocale }) {
  const { getSystemSetting } = useSystemSettings()
  const extraParams = getSystemSetting('api.v2.brands.index.menu.ecom.query')
  // handle cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('brands')

  // internal state
  const [brands, setBrands] = useBrandsState([])
  const [brandsChecksum, setBrandsChecksum] = useBrandsChecksumState('')
  const [brandsReady, setBrandsReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  // get cache with current locale
  const availableLocales = useMemo(() => (
    _.get(config, 'locale.availableLocales')
  ), [config])
  const currentLocaleData = useMemo(() => (
    _.get(brands, currentLocale, [])
  ), [currentLocale, brands])
  const currentLocaleChecksum = useMemo(() => (
    _.get(brandsChecksum, currentLocale, '')
  ), [currentLocale, brandsChecksum])

  function apiFetchBrands(page = 1) {
    return fetchBrands({
      includes: [
        'category_ids'
      ].join(','),
      page,
      ...extraParams

    })
  }

  function fetchBrandsAll(params) {
    return getAllDataInParallel({
      fn: (args) => fetchBrands({
        ...params,
        ...args,
      }),
      key: 'brands'
    })
  }

  /**
   * handleFetchBrands
   * get department with category ids for caching, will be used for making menu
   */
  async function handleFetchBrands() {
    // use data in cache first
    if (
      !_.isEmpty(currentLocaleData) &&
      currentLocaleChecksum === cacheChecksumValue
    ) {
      setBrandsReady(true)
      setEnableRefresh(true)
      return
    }

    try {
      setEnableRefresh(false)
      const { cacheChecksum, brands: data } = await apiFetchBrands()
      setBrands({
        ..._.pick(brands, availableLocales),
        // detail can be very large to store in local storage
        [currentLocale]: data.map((item) => (
          _.omit(item, ['details', 'detailsPlainText'])
        ))
      })
      setBrandsReady(true)
      setEnableRefresh(true)
      setBrandsChecksum({
        ..._.pick(brandsChecksum, availableLocales),
        [currentLocale]: cacheChecksum
      })
    } catch (error) {
      console.warn('fail to load brands for cache: ', error)
    }
  }

  /**
   * handleRefreshBrandsCache
   * refresh departments cache when checksum is changed
   */
  async function handleRefreshBrandsCache() {
    // when it is not required to update cache, skip the process
    if (!enableRefresh || currentLocaleChecksum === cacheChecksumValue) {
      if (_.isEmpty(currentLocaleChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setBrandsChecksum({
          ..._.pick(brandsChecksum, availableLocales),
          [currentLocale]: cacheChecksumValue
        })
      }
      return
    }

    try {
      // start refreshing cache data
      setEnableRefresh(false)
      const { brands: data, cacheChecksum } = await apiFetchBrands()
      setBrands({
        ..._.pick(brands, availableLocales),
        // detail can be very large to store in local storage
        [currentLocale]: data.map((item) => (
          _.omit(item, ['details', 'detailsPlainText'])
        ))
      })
      setBrandsChecksum({
        ..._.pick(brandsChecksum, availableLocales),
        [currentLocale]: cacheChecksum
      })
    } catch (error) {
      console.warn('fail to refresh brands for cache', error)
    } finally {
      setEnableRefresh(true)
    }
  }

  /**
   * get all department from omnitech API when app is mounted
   */
  useEffect(() => {
    handleFetchBrands()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchBrands'])
    }
  }, [])

  /**
   * update department cache base on cache checksum
   */
  useEffect(() => {
    if (!brandsReady || _.isUndefined(cacheChecksumValue)) return
    handleRefreshBrandsCache()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchBrands'])
    }
  }, [brandsReady, cacheChecksumValue, currentLocaleChecksum])

  const state = {
    brands: currentLocaleData,
    brandsReady,
    fetchBrands,
    fetchBrandsAll,
  }

  return (
    <BrandsContext.Provider value={state}>
      {children}
    </BrandsContext.Provider>
  )
};
